import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CreateSponsor from "./CreateSponsor";
import type { Sponsor } from "../../types/deal";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import { Alert, CircularProgress, Box } from "@mui/material";

type LoadResult = {
  /** Route id this result belongs to; a different id means still loading. */
  id: string | undefined;
  sponsor: Partial<Sponsor> | null;
  error: string | null;
};

const EditSponsorWrapper = () => {
  const { sponsorId } = useParams();
  const [result, setResult] = useState<LoadResult | null>(null);
  // Never show the previous sponsor (or a blank Create form) while a new id loads
  const loading = !result || result.id !== sponsorId;

  useEffect(() => {
    let cancelled = false;

    const fetchSponsor = async () => {
      try {
        const response = await apiGetById("sponsor", Number(sponsorId)); // API call
        if (cancelled) return;
        if (response && response.success && response.data?.[0]) {
          setResult({ id: sponsorId, sponsor: response.data[0], error: null });
        } else {
          setResult({
            id: sponsorId,
            sponsor: null,
            error:
              !response?.success && response?.status !== 404
                ? "Failed to load the sponsor. Please try again."
                : "Sponsor not found.",
          });
        }
      } catch (error) {
        console.error("Failed to fetch sponsor", error);
        if (!cancelled) {
          setResult({
            id: sponsorId,
            sponsor: null,
            error: "Failed to load the sponsor. Please try again.",
          });
        }
      }
    };

    fetchSponsor();
    return () => {
      cancelled = true;
    };
  }, [sponsorId]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Without a loaded record the form would act as Create (and POST a new one)
  if (result.error || !result.sponsor) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{result.error || "Sponsor not found."}</Alert>
      </Box>
    );
  }

  return <CreateSponsor key={sponsorId} sponsor={result.sponsor} isEdit={true} />;
};

export default EditSponsorWrapper;
